import { alpha, Box, Typography, useTheme } from '@mui/material'
import { memo, ReactNode, useEffect, useMemo, useRef } from 'react'
import LiveText from '../common/LiveText'
import {
    buildPayoffGraphMetaLiveSubscribe,
    getPayoffCallDeltaColorSelector,
    getPayoffCallDeltaSelector,
    getPayoffCallOiSelector,
    getPayoffExpectedPnlColorSelector,
    getPayoffExpectedPnlSelector,
    getPayoffExpiryPnlColorSelector,
    getPayoffExpiryPnlSelector,
    getPayoffPutDeltaColorSelector,
    getPayoffPutDeltaSelector,
    getPayoffPutOiSelector,
    getPayoffStrikeSelector
} from './payoffGraphLiveDisplay'
import { getPayoffGraphMetaData } from './payoffGraphMetaDataStore'

export type { PayoffGraphMetaData } from './payoffGraphMetaDataStore'

export type Props = {
    metaDataKey: string
    showOiBars?: boolean
}

const CELL_WIDTH = {
    strike: 72,
    oi: 96,
    pnl: 108
} as const

const CELL_HEIGHT = 38
const VALUE_ROW_HEIGHT = 20
const VALUE_FONT_SIZE = 13

const valueTypographySx = {
    fontSize: VALUE_FONT_SIZE,
    lineHeight: `${VALUE_ROW_HEIGHT}px`,
    fontWeight: 500
} as const

const liveValueStyle = {
    ...valueTypographySx,
    height: VALUE_ROW_HEIGHT,
    display: 'block'
} as const

const LegendLabel = ({ children, align = 'right' }: { children: ReactNode; align?: 'left' | 'right' }) => (
    <Typography
        variant="caption"
        component="span"
        color="text.secondary"
        className="trade-panel-text"
        sx={{ display: 'block', textAlign: align, lineHeight: '14px', height: 14 }}
    >
        {children}
    </Typography>
)

const LEGEND_BORDER_OPACITY = 0.35

type LegendSectionProps = {
    label: string
    width: number
    children: ReactNode
    showDivider?: boolean
    borderColor: string
    align?: 'left' | 'right'
}

const LegendSection = ({ label, width, children, showDivider = true, borderColor, align = 'right' }: LegendSectionProps) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width,
            height: CELL_HEIGHT,
            flexShrink: 0,
            px: 1,
            boxSizing: 'border-box',
            borderRight: showDivider ? 1 : 0,
            borderColor
        }}
    >
        <LegendLabel align={align}>{label}</LegendLabel>
        <Box
            sx={{
                height: VALUE_ROW_HEIGHT,
                display: 'flex',
                justifyContent: align === 'left' ? 'flex-start' : 'flex-end',
                alignItems: 'center'
            }}
        >
            {children}
        </Box>
    </Box>
)

const OiValueRow = ({
    metaDataKey,
    oiSelector,
    deltaSelector,
    deltaColorSelector,
    color
}: {
    metaDataKey: string
    oiSelector: ReturnType<typeof getPayoffCallOiSelector>
    deltaSelector: ReturnType<typeof getPayoffCallDeltaSelector>
    deltaColorSelector: ReturnType<typeof getPayoffCallDeltaColorSelector>
    color: string
}) => {
    const subscribe = useMemo(() => buildPayoffGraphMetaLiveSubscribe(metaDataKey), [metaDataKey])

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
                width: '100%',
                height: VALUE_ROW_HEIGHT
            }}
        >
            <LiveText
                subscribe={subscribe}
                selectText={oiSelector}
                className="font-feature-num"
                style={{
                    ...liveValueStyle,
                    color,
                    textAlign: 'right',
                    flexShrink: 0
                }}
            />
            <LiveText
                subscribe={subscribe}
                selectText={deltaSelector}
                selectColor={deltaColorSelector}
                className="font-feature-num"
                style={{
                    fontSize: 10,
                    lineHeight: `${VALUE_ROW_HEIGHT}px`,
                    fontWeight: 500,
                    textAlign: 'right',
                    flexShrink: 0,
                    minWidth: 32
                }}
            />
        </Box>
    )
}

const PayoffGraphMetaDataDisplay = ({ metaDataKey, showOiBars = true }: Props) => {
    const theme = useTheme()
    const callColor = theme.palette.call.main
    const putColor = theme.palette.put.main
    const legendBorderColor = alpha(theme.palette.divider, LEGEND_BORDER_OPACITY)
    const containerRef = useRef<HTMLDivElement>(null)

    const subscribe = useMemo(() => buildPayoffGraphMetaLiveSubscribe(metaDataKey), [metaDataKey])

    const strikeSelector = useMemo(() => getPayoffStrikeSelector(metaDataKey), [metaDataKey])
    const callOiSelector = useMemo(() => getPayoffCallOiSelector(metaDataKey), [metaDataKey])
    const callDeltaSelector = useMemo(() => getPayoffCallDeltaSelector(metaDataKey), [metaDataKey])
    const putOiSelector = useMemo(() => getPayoffPutOiSelector(metaDataKey), [metaDataKey])
    const putDeltaSelector = useMemo(() => getPayoffPutDeltaSelector(metaDataKey), [metaDataKey])
    const expiryPnlSelector = useMemo(() => getPayoffExpiryPnlSelector(metaDataKey), [metaDataKey])
    const expectedPnlSelector = useMemo(() => getPayoffExpectedPnlSelector(metaDataKey), [metaDataKey])
    const expiryPnlColorSelector = useMemo(() => getPayoffExpiryPnlColorSelector(metaDataKey), [metaDataKey])
    const expectedPnlColorSelector = useMemo(() => getPayoffExpectedPnlColorSelector(metaDataKey), [metaDataKey])
    const callDeltaColorSelector = useMemo(
        () => getPayoffCallDeltaColorSelector(metaDataKey, callColor),
        [metaDataKey, callColor]
    )
    const putDeltaColorSelector = useMemo(
        () => getPayoffPutDeltaColorSelector(metaDataKey, putColor),
        [metaDataKey, putColor]
    )

    useEffect(() => {
        const applyVisibility = () => {
            const el = containerRef.current
            if (!el) return
            el.style.display = getPayoffGraphMetaData(metaDataKey) ? 'inline-flex' : 'none'
        }
        applyVisibility()
        return subscribe(applyVisibility)
    }, [metaDataKey, subscribe])

    return (
        <Box
            ref={containerRef}
            sx={{
                display: 'inline-flex',
                alignItems: 'stretch',
                border: 1,
                borderColor: legendBorderColor,
                borderRadius: 1,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                height: CELL_HEIGHT
            }}
        >
            <LegendSection label="STRIKE" width={CELL_WIDTH.strike} borderColor={legendBorderColor} align="left">
                <LiveText
                    subscribe={subscribe}
                    selectText={strikeSelector}
                    className="font-feature-num"
                    style={{ ...liveValueStyle, textAlign: 'left' }}
                />
            </LegendSection>

            {showOiBars ? (
                <>
                    <LegendSection label="CE OI" width={CELL_WIDTH.oi} borderColor={legendBorderColor}>
                        <OiValueRow
                            metaDataKey={metaDataKey}
                            oiSelector={callOiSelector}
                            deltaSelector={callDeltaSelector}
                            deltaColorSelector={callDeltaColorSelector}
                            color={callColor}
                        />
                    </LegendSection>
                    <LegendSection label="PE OI" width={CELL_WIDTH.oi} borderColor={legendBorderColor}>
                        <OiValueRow
                            metaDataKey={metaDataKey}
                            oiSelector={putOiSelector}
                            deltaSelector={putDeltaSelector}
                            deltaColorSelector={putDeltaColorSelector}
                            color={putColor}
                        />
                    </LegendSection>
                </>
            ) : null}

            <LegendSection label="EXPIRY P/L" width={CELL_WIDTH.pnl} borderColor={legendBorderColor}>
                <LiveText
                    subscribe={subscribe}
                    selectText={expiryPnlSelector}
                    selectColor={expiryPnlColorSelector}
                    className="font-feature-num"
                    style={{ ...liveValueStyle, textAlign: 'right' }}
                />
            </LegendSection>

            <LegendSection label="EXPECTED P/L" width={CELL_WIDTH.pnl} showDivider={false} borderColor={legendBorderColor}>
                <LiveText
                    subscribe={subscribe}
                    selectText={expectedPnlSelector}
                    selectColor={expectedPnlColorSelector}
                    className="font-feature-num"
                    style={{ ...liveValueStyle, textAlign: 'right' }}
                />
            </LegendSection>
        </Box>
    )
}

export default memo(PayoffGraphMetaDataDisplay)
